import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Globe, ChevronDown } from "lucide-react";

const LANGS = [
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "ar", label: "العربية", flag: "🇸🇦" },
  { code: "zh-CN", label: "中文", flag: "🇨🇳" },
  { code: "pt", label: "Português", flag: "🇧🇷" },
  { code: "it", label: "Italiano", flag: "🇮🇹" },
  { code: "ht", label: "Kreyòl", flag: "🇭🇹" },
];

function setCookie(name, value, days = 365) {
  const exp = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${value}; path=/; expires=${exp}; SameSite=Lax`;
  const host = window.location.hostname;
  const parts = host.split(".");
  if (parts.length > 1) {
    const root = "." + parts.slice(-2).join(".");
    document.cookie = `${name}=${value}; path=/; domain=${root}; expires=${exp}; SameSite=Lax`;
  }
}

function readCurrentLang() {
  const m = document.cookie.match(/googtrans=\/[^/]+\/([^;]+)/);
  return m ? m[1] : "fr";
}

export default function LangToggle({ className = "" }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("fr");
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    setCurrent(readCurrentLang());
  }, []);

  useEffect(() => {
    if (window.__googleTranslateInjected) return;
    window.__googleTranslateInjected = true;

    window.googleTranslateElementInit = function () {
      // eslint-disable-next-line no-new, no-undef
      new window.google.translate.TranslateElement(
        {
          pageLanguage: "fr",
          includedLanguages: LANGS.map((l) => l.code).join(","),
          autoDisplay: false,
          layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
        },
        "google_translate_hidden"
      );
    };

    const s = document.createElement("script");
    s.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    s.async = true;
    document.body.appendChild(s);
  }, []);

  useLayoutEffect(() => {
    if (open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const menuWidth = 224; // ~ w-56
      let left = r.right - menuWidth;
      if (left < 8) left = 8;
      if (left + menuWidth > window.innerWidth - 8) left = window.innerWidth - menuWidth - 8;
      setPos({ top: r.bottom + 8, left });
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onClickAway = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener("mousedown", onClickAway);
    document.addEventListener("touchstart", onClickAway);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onClickAway);
      document.removeEventListener("touchstart", onClickAway);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  const switchTo = (code) => {
    if (code === "fr") {
      setCookie("googtrans", "");
      window.location.reload();
      return;
    }
    setCookie("googtrans", `/fr/${code}`);
    setCurrent(code);
    window.location.reload();
  };

  const cur = LANGS.find((l) => l.code === current) || LANGS[0];

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 bg-surface hover:bg-white border border-[#E2E8F0] rounded-full text-xs font-bold text-navy transition-colors notranslate ${className}`}
        data-testid="lang-toggle-btn"
        aria-label="Change language"
      >
        <Globe size={14} />
        <span>{cur.flag}</span>
        <span className="uppercase tracking-wide">{cur.code === "zh-CN" ? "ZH" : cur.code.slice(0, 2)}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && typeof document !== "undefined" && createPortal(
        <div
          ref={menuRef}
          style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 9999 }}
          className="w-56 bg-white border border-[#E2E8F0] rounded-2xl shadow-2xl py-2 notranslate"
          data-testid="lang-menu"
        >
          {LANGS.map((l) => (
            <button
              key={l.code}
              onClick={() => switchTo(l.code)}
              className={`w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-surface transition-colors ${current === l.code ? "bg-lime/10 font-black text-navy" : "text-navy/80"}`}
              data-testid={`lang-option-${l.code}`}
            >
              <span className="text-xl">{l.flag}</span>
              <span className="flex-1 text-sm font-bold">{l.label}</span>
              {current === l.code && <span className="text-lime">✓</span>}
            </button>
          ))}
          <div className="border-t border-[#E2E8F0] mt-2 pt-2 px-4 pb-1">
            <p className="text-[10px] text-[#94A3B8]">Powered by Google Translate</p>
          </div>
        </div>,
        document.body
      )}

      {/* Hidden host required by Google Translate */}
      <div id="google_translate_hidden" style={{ display: "none" }} />
    </>
  );
}
